/*
 * @author BSG <dev@bsgroup.eu>
 * @copyright Better Software Group S.A.
 * @version: 1.0
 */

import { useCallback, useEffect, useState } from "react";
import {
  ApiErrors,
  AuthStore,
  buildEmailRule,
  buildPasswordMaximumRule,
  buildPasswordMinimumRule,
  DeviceHelper,
  dispatch,
  IAuthRequestModel,
  IFormValues,
  IUserSubprofileModel,
  offeringIdsService,
  StorageHelper,
  updateApiErrors,
  useAuthSelector,
  useInitialHomePage,
  userProfileHelper,
  UserStore,
} from "@nf/common";
import { ROUTES } from "@nf/constants";
import { Trans, useTranslation } from "next-i18next";
import dynamic from "next/dynamic";
import axios from "axios";

import { Input } from "components/Inputs/Input";
import { InputPassword } from "components/Inputs/InputPassword";
import { LabelField } from "components/Form/LabelField";
import { FormButton } from "components/Form/FormButton";
import CommonTextError from "components/CommonTextError";
import LinkText from "components/LinkText";
import LinkWithLocale from "components/LinkWithLocale";
import { Divider } from "../../Divider";
import {
  useCountry,
  useForgotPasswordLink,
  useLanguage,
  useRedirectToProfileScreen,
  useRedirectUrlParam,
  useRouter,
  useSetCurrentSubProfile,
  useSignUpUrl,
  useUserSubscriptions,
} from "../../../hooks";
import { useIsLoading } from "helpers/setIsLoadingContext";
import { googleAnalyticsEvent } from "../../../lib/gtm";

import {
  CUSTOMER_ALREADY_EXISTS_PARAM,
  CUSTOMER_ALREADY_VALIDATED_PARAM,
} from "constants/param";
import {
  MOBILE_PARAM_SUBSCRIPTION_VALUE,
  MOBILE_PARAM_SUBSCRIPTIONS,
} from "../../../screens/WelcomeScreen/WelcomeScreen";

import styles from "./LoginForm.module.scss";

const Form = dynamic<IFormValues>(() => import("components/Form/Form"));

const EMAIL_FIELD = "email";
const PASSWORD_FIELD = "password";

const FORM_FIELDS = [EMAIL_FIELD, PASSWORD_FIELD];

export const ForgotPasswordLink = () => {
  const { t } = useTranslation("translations");
  const forgotPasswordLink = useForgotPasswordLink();
  return (
    <LinkWithLocale href={forgotPasswordLink}>
      <a className={styles.resetPassword}>
        <Trans i18nKey={t("web_login_password_forgot", "")} />
      </a>
    </LinkWithLocale>
  );
};

export const LoginForm = () => {
  const router = useRouter();
  const { setIsLoading } = useIsLoading();
  const { t } = useTranslation("translations");
  const [isFormSent, setIsFormSent] = useState(false);
  const [apiErrors, setApiErrors] = useState<ApiErrors>({});
  const { isLoading, error } = useAuthSelector();
  const [autoCompleted, setAutoCompleted] = useState(false);
  const [errorDisplay, setErrorDisplay] = useState<string | null>(null);
  const { email, message } = router.query;
  const isUserAlreadyExist = message?.includes(CUSTOMER_ALREADY_EXISTS_PARAM);
  const isUserAlreadyValidated = message?.includes(
    CUSTOMER_ALREADY_VALIDATED_PARAM
  );

  const country = useCountry();
  const language = useLanguage();

  const redirectUrl = useRedirectUrlParam();
  const { hasActiveSubscriptions } = useUserSubscriptions();
  const setCurrentProfile = useSetCurrentSubProfile();
  const { getInitialHomePage } = useInitialHomePage();
  const redirectToProfileSelectionScreen = useRedirectToProfileScreen();

  const initialValues = {
    email,
  };

  const redirectAfterSignIn = useCallback(
    (subProfiles?: IUserSubprofileModel[], pinCode?: string) => {
      if (redirectUrl) {
        const owner = userProfileHelper.findMainProfile(subProfiles);

        if (owner && !pinCode) {
          setCurrentProfile(owner).then(() => router.replace(redirectUrl));
        } else {
          redirectToProfileSelectionScreen();
        }
        return;
      }

      if (DeviceHelper.isMobile()) {
        router.replace(
          hasActiveSubscriptions
            ? {
                pathname: ROUTES.WELCOME,
                query: {
                  [MOBILE_PARAM_SUBSCRIPTIONS]: MOBILE_PARAM_SUBSCRIPTION_VALUE,
                },
              }
            : {
                pathname: ROUTES.PAYMENT_SUBSCRIPTION,
              }
        );
        return;
      }

      if (subProfiles?.length === 1) {
        const initialHomePage = getInitialHomePage(subProfiles[0]);
        setCurrentProfile(subProfiles[0]).then(() =>
          router.replace(initialHomePage)
        );
        return;
      }

      if (subProfiles?.length && subProfiles.length > 1) {
        redirectToProfileSelectionScreen();
      }
    },
    [
      redirectUrl,
      hasActiveSubscriptions,
      router,
      redirectToProfileSelectionScreen,
      getInitialHomePage,
    ]
  );

  const onFinish = async (values: IFormValues) => {
    const payload: IAuthRequestModel = {
      Username: values[EMAIL_FIELD],
      Password: values[PASSWORD_FIELD],
      Country: country,
      Language: language,
    };

    try {
      setIsLoading(true);
      setIsFormSent(true);

      const deviceInfo = await DeviceHelper.getDeviceInfo();
      const { data } = await axios.post(ROUTES.API_SIGN_IN, {
        ...payload,
        Device: deviceInfo,
      });

      StorageHelper.setSession(data.authResponse.AuthorizationToken);
      googleAnalyticsEvent.login();

      dispatch(
        AuthStore.Actions.signInWebSuccess(data.authResponse.User, {
          ...data.authResponse.AuthorizationToken,
          IsOverTheDeviceLimit: data.session.overTheDeviceLimit,
        })
      );
      dispatch(AuthStore.Actions.getSessionDetailsSuccess(data.session));
      dispatch(
        UserStore.Actions.getUserSubscriptionsSuccess(data.subscriptions)
      );
      dispatch(
        UserStore.Actions.getUserSubprofilesSuccess(
          data.subProfiles,
          data.currentSubProfile,
          data?.pinCode
        )
      );
      dispatch(
        UserStore.Actions.getUserAvailabilityKeysSuccess(data.availabilityKeys)
      );
      dispatch(UserStore.Actions.getUserConsentsSuccess(data.consents));
      dispatch(UserStore.Actions.getUserFavorites());
      data.products && offeringIdsService.setOfferingIds(data.products);
      StorageHelper.setTvRefreshTokens(data.tvRefreshToken);

      redirectAfterSignIn(data.subProfiles, data.pinCode);
    } catch (e: any) {
      setIsLoading(false);
      setIsFormSent(false);
      setGlobalErrorMessage("WRONG_EMAIL_OR_PASSWORD");
    }
  };

  const setGlobalErrorMessage = useCallback(
    (error: string | null) => {
      if (error === "WRONG_EMAIL_OR_PASSWORD") {
        setErrorDisplay("web_login_failed_error");
      } else {
        setErrorDisplay(error);
      }
      setApiErrors({
        ...apiErrors,
        Login: [error || ""],
      });
    },
    [apiErrors]
  );

  const onValuesChange = (changedValues: IFormValues) => {
    const [isUpdated, newApiErrors] = updateApiErrors(apiErrors, changedValues);

    if (isUpdated) setApiErrors(newApiErrors);

    setApiErrors({
      ...apiErrors,
      Login: [""],
    });
    isFormSent && setIsFormSent(false);
  };

  useEffect(() => {
    if (error && isFormSent) {
      setGlobalErrorMessage(error.Message as string);
      setIsLoading(false);
    }
  }, [error, isFormSent]);

  const checkIfAutoCompleted = () => setAutoCompleted(true);
  const signUpUrl = useSignUpUrl();

  return (
    <Form
      initialValues={initialValues}
      className={styles.form}
      onFinish={onFinish}
      onValuesChange={onValuesChange}
    >
      <h1 className={styles.title}>{t("web_login_header", "")}</h1>

      {isUserAlreadyExist && (
        <p className={styles.userExist}>{t("web_user_exist", "")}</p>
      )}

      {isUserAlreadyValidated && (
        <p className={styles.userValidated}>
          {t("web_user_already_validated")}
        </p>
      )}

      <LabelField
        className={styles.labelFieldContainer}
        name="email"
        rules={[buildEmailRule(t)]}
        apiErrors={apiErrors.email || []}
        validateTrigger={["onBlur", "onSubmit"]}
      >
        <Input
          autoFocus
          spellCheck="false"
          trimValue
          placeholder={t("web_login_email_field", "")}
          checkIfAutoCompleted={checkIfAutoCompleted}
        />
      </LabelField>

      <LabelField
        className={styles.labelFieldContainer}
        name="password"
        rules={[buildPasswordMinimumRule(t), buildPasswordMaximumRule(t)]}
        tip={<ForgotPasswordLink />}
        validateTrigger={["onBlur", "onSubmit"]}
      >
        <InputPassword
          spellCheck="false"
          placeholder={t("web_login_password_field", "")}
          checkIfAutoCompleted={checkIfAutoCompleted}
          visibilityToggle
        />
      </LabelField>

      <FormButton
        nonEmptyFields={{ requiredFields: FORM_FIELDS, autoCompleted }}
        loading={isLoading}
        disabled={isLoading || isFormSent}
        className={styles.button}
      >
        {t("web_login_button", "")}
      </FormButton>

      {errorDisplay && !isLoading && (
        <div className="mt-md mb-md">
          <CommonTextError centered translationKey={errorDisplay || ""} />
        </div>
      )}
      <Divider />
      <p className="mt-md">
        {t("web_login_create_account", "")}
        <LinkText className={styles.link} bold href={signUpUrl}>
          {t("web_create_account_link", "")}
        </LinkText>
      </p>
    </Form>
  );
};

export default LoginForm;
